import React, { useEffect, useState } from 'react'
import { View, Text } from 'react-native'

import spacing from '../../styles/spacing'
import SingleFile from './SingleFile'
import MediaHelper from '../../libraries/mediaHelper'
import { viewMode } from '../../screens/EntityAddScreen'
import EntityField from '../EntityField'
import { EntityTypePicker } from './EntityTypePicker'
import { useDiscoverEntityEntityType } from '../../hooks/useDiscoverEntityEntityType'
import { DoubleFieldListMapper } from './DoubleFieldListMapper'
import MSFESButton from '../MSFESButton'
import api from '../../data/api'
import { uniqBy } from 'lodash'
import MSFESLabel from '../MSFESLabel'
import { useSelector } from 'react-redux'
import { ValueToTenantMapper } from './ValueToTenantMapper'
import MSFESHeading from '../MSFESHeading'

export const CustomFunctionalityXMLFieldMapper = (props) => {
  const environment = useSelector((state) => state.app.environment)
  const [localValue, setLocalValue] = useState(props.defaultValue)

  const [mappableFields, setMappableFields] = useState([])
  const [mappedChildEntityTypes, setMappedChildEntityTypes] = useState([])
  const calculatedStyle = [props.style, { padding: spacing.m1, height: null }]

  const includedProps = { editable: props.mode !== viewMode }

  useEffect(() => {
    props.onChangeText(localValue)
  }, [localValue])

  useEffect(() => {
    const file = localValue?.file
    if (file) {
      const filePath = file.path[0] || ''
      MediaHelper.getBlobFromDataURL(filePath).then(async (blob) => {
        const text = await blob.text()
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(text, 'application/xml')

        // give me an xpath (?) to get all of the DOC.ROW entries in the file.
        const rows = xmlDoc.evaluate(
          localValue?.rowXPath,
          xmlDoc,
          null,
          XPathResult.ANY_TYPE,
          null
        )

        let rowNodes = rows.iterateNext()
        let isFirst = true
        while (rowNodes) {
          // get all of the top level fields from this row. These are the mappable fields.
          const fields = rowNodes.children

          if (isFirst) {
            let firstRowFields = []
            for (let i = 0; i < fields.length; i++) {
              const fieldName = fields[i].tagName
              firstRowFields.push({ id: fieldName, title: fieldName })

              const children = fields[i].children
              for (let j = 0; j < children.length; j++) {
                const child = children[j]
                const childName = child.tagName

                firstRowFields.push({
                  id: fieldName + '_' + childName,
                  title: fieldName + '.*.' + childName
                })

                if (child.children) {
                  for (let k = 0; k < child.children.length; k++) {
                    const grandChild = child.children[k]
                    const grandChildName = grandChild.tagName
                    firstRowFields.push({
                      id: fieldName + '_' + childName + '_' + grandChildName,
                      title:
                        fieldName + '.*.' + childName + '.' + grandChildName
                    })
                  }
                }
              }
            }
            firstRowFields = uniqBy(firstRowFields, 'id')
            setMappableFields(firstRowFields)
            isFirst = false
          } else {
            // these are subsequent rows, and may let us find fields not in the first.
            for (let i = 0; i < fields.length; i++) {
              const fieldName = fields[i].tagName
              if (!mappableFields.find((f) => f.id === fieldName)) {
                setMappableFields((mappableFields) => [
                  ...mappableFields,
                  { id: fieldName, title: fieldName }
                ])
              }
            }
          }

          rowNodes = rows.iterateNext()
        }
      })
    }
  }, [localValue?.file?.path])

  const emailSuffix =
    environment === 'prod' ? '@data.emberfd.com' : '@staging-data.emberfd.com'

  const { entityType: entityTypeSelected } = useDiscoverEntityEntityType(
    null,
    null,
    null,
    localValue?.entity_type_id
  )

  useEffect(() => {
    const fieldMap = localValue?.field_map
    if (fieldMap) {
      // const childEntityTypes = fieldMap.map((f) => f.field_data.params.sourceName)
      // setMappedChildEntityTypes(childEntityTypes)
      const mappedDestinationFields = fieldMap.map((f) => {
        return f.destination?.id
      })
      const mappedDestinationFieldsWithType = mappedDestinationFields
        .map((f) => {
          const entityTypeField = entityTypeSelected?.fields?.find(
            (field) => field.field_data?.property == f
          )
          return entityTypeField
        })
        .filter((f) => f?.field_data?.type == 'view-list-inline')

      setMappedChildEntityTypes(mappedDestinationFieldsWithType)
    }
  }, [localValue?.field_map, entityTypeSelected])

  return (
    <View style={calculatedStyle}>
      <View>
        <MSFESLabel label={'Enabled'} />

        <EntityField
          name={'enabled'}
          field={{
            title: 'Enabled',
            field_data: { property: 'enabled', type: 'boolean' }
          }}
          errorMessage={''}
          showLabel={false}
          mode={props.mode}
          entity={{ enabled: true, ...localValue }}
          fieldChanged={(field, newValue, isUserInitiated) => {
            if (isUserInitiated) {
              setLocalValue((_localValue) => ({
                ..._localValue,
                [field.field_data.property]: newValue
              }))
            }
          }}
        />
      </View>

      <View>
        <MSFESLabel label={`Importer Email (${emailSuffix})`} required={true} />
        <EntityField
          name={'importerEmailPrefix'}
          errorMessage={''}
          showLabel={false}
          entity={localValue}
          fieldChanged={(field, newValue, isUserInitiated) => {
            // format to only [a-z0-9-_]
            if (isUserInitiated) {
              const formattedValue = newValue.replace(/[^a-z0-9-_]/g, '')

              setLocalValue((_localValue) => ({
                ..._localValue,
                [field.field_data.property]: formattedValue
              }))
            }
          }}
          field={{
            title: 'Importer Email',
            field_data: { property: 'importerEmail', type: 'single-text' }
          }}
          mode={props.mode}
          {...includedProps}
        />
      </View>
      <View>
        <MSFESLabel label={`Importer Folder (SFTP)`} required={true} />
        <EntityField
          name={'importerFolder'}
          errorMessage={''}
          showLabel={false}
          entity={localValue}
          fieldChanged={(field, newValue, isUserInitiated) => {
            if (isUserInitiated) {
              // format to only [a-z0-9-_/]
              setLocalValue((_localValue) => ({
                ..._localValue,
                [field.field_data.property]: newValue
              }))
            }
          }}
          field={{
            title: 'Importer Folder',
            field_data: { property: 'importerFolder', type: 'single-text' }
          }}
          mode={props.mode}
          {...includedProps}
        />
      </View>
      <View>
        <MSFESLabel label={'File Upload'} required={true} />
        <SingleFile
          value={localValue?.file?.value}
          savesAgainstEntityId={false}
          onBusyStateChanged={() => {}}
          pathValue={localValue?.file?.path}
          onChangeText={(value, path) => {
            setLocalValue({ ...localValue, file: { value, path } })
          }}
          data={{
            field: {
              field_data: {
                params: {
                  fileType: MediaHelper.DocumentType,
                  allowMultipleUploads: false,
                  canSelectFromLibrary: false
                }
              }
            }
          }}
          isRestricted={false}
          {...includedProps}
        />
      </View>

      <EntityField
        name={'rowXPath'}
        errorMessage={''}
        entity={localValue}
        fieldChanged={(field, newValue, isUserInitiated) => {
          if (isUserInitiated) {
            setLocalValue((_localValue) => ({
              ..._localValue,
              [field.field_data.property]: newValue
            }))
          }
        }}
        field={{
          title: 'Row XPath',
          field_data: { property: 'rowXPath', type: 'single-text' }
        }}
        mode={props.mode}
        {...includedProps}
      />

      <View>
        <MSFESLabel label={'Map Into Entity Type'} required={true} />
        <EntityTypePicker
          mode={props.mode}
          placeholder="Select the Map Into Entity Type"
          currentEntityType={localValue?.entity_type_id}
          onEntityTypeSelected={(entityType) => {
            setLocalValue((_localValue) => ({
              ..._localValue,
              entity_type_id: entityType?.id
            }))
          }}
        />
      </View>
      <View>
        <MSFESLabel label={'Unique Key'} required={true} />

        <EntityField
          name={'uniqueKey'}
          field={{
            title: 'Unique Key',
            field_data: { property: 'uniqueKey', type: 'select-field' }
          }}
          fieldsSupplied={entityTypeSelected?.fields}
          errorMessage={''}
          showLabel={false}
          mode={props.mode}
          entity={localValue}
          fieldChanged={(field, newValue, isUserInitiated) => {
            if (isUserInitiated) {
              setLocalValue((_localValue) => ({
                ..._localValue,
                [field.field_data.property]: newValue
              }))
            }
          }}
        />
      </View>
      <View>
        <MSFESLabel label={'Timezone'} required={true} />

        <EntityField
          name={'timezone'}
          field={{
            title: 'Timezone',
            field_data: { property: 'timezone', type: 'select-timezone' }
          }}
          errorMessage={''}
          showLabel={false}
          mode={props.mode}
          entity={localValue}
          fieldChanged={(field, newValue, isUserInitiated) => {
            if (isUserInitiated) {
              setLocalValue((_localValue) => ({
                ..._localValue,
                [field.field_data.property]: newValue
              }))
            }
          }}
        />
      </View>

      <DoubleFieldListMapper
        fieldList1={mappableFields}
        fieldList2={entityTypeSelected?.fields?.map((f) => ({
          id: f.field_data.property,
          title: f.field_data.title
        }))}
        mode={props.mode}
        entityTypeFieldList2={entityTypeSelected}
        mappedFields={localValue?.field_map}
        onFieldmapChanged={(fieldmap) => {
          setLocalValue((_localValue) => ({
            ..._localValue,
            field_map: fieldmap
          }))
        }}
      />
      <MSFESHeading
        style={{
          backgroundColor: 'transparent',
          paddingTop: spacing.m2,
          paddingHorizontal: spacing.m1
        }}
      >
        Tenant Mapping (Optional)
      </MSFESHeading>
      <ValueToTenantMapper
        mode={props.data?.entity?.tenant_id ? 'view' : props.mode}
        value={localValue?.valueToTenantMap}
        xmlFields={mappableFields}
        onChange={(valueToTenantMap) => {
          setLocalValue((_localValue) => ({
            ..._localValue,
            valueToTenantMap
          }))
        }}
      />
      <MSFESHeading
        style={{
          backgroundColor: 'transparent',
          paddingTop: spacing.m2,
          paddingHorizontal: spacing.m1
        }}
      >
        Child Unique Keys
      </MSFESHeading>
      <View>
        <MSFESLabel label={'Child Unique Keys needed'} required={true} />
        {mappedChildEntityTypes.map((f, index) => (
          <ChildUniqueKeyMapper
            mode={props.mode}
            key={index}
            field={f}
            entity={localValue}
            fieldChanged={(field, newValue, isUserInitiated) => {
              if (isUserInitiated) {
                setLocalValue((_localValue) => ({
                  ..._localValue,
                  [field.field_data.property]: newValue
                }))
              }
            }}
          />
        ))}
      </View>
      <MSFESButton
        title="Test Import"
        disabled={props.mode === viewMode}
        onPress={() => {
          api
            .post(`/xml-import/${props?.data?.entity?.id}`, {
              file: localValue?.file?.path[0]
            })
            .then((result) => {
              console.info(result)
            })
            .catch((error) => {
              console.warn(error)
            })
        }}
      />

      <Text>{localValue?.file?.path[0]}</Text>
    </View>
  )
}

const ChildUniqueKeyMapper = (props) => {
  const { field, fieldChanged, entity } = props
  const { entityType: childEntityType } = useDiscoverEntityEntityType(
    null,
    null,
    field.field_data?.params?.sourceName,
    null
  )

  return (
    <View>
      <MSFESLabel label={field.field_data?.title} required={true} />
      <EntityField
        name={'uniqueKey'}
        field={{
          title: 'Unique Key in Output Entity',
          field_data: {
            property: 'key_' + field.field_data?.property,
            type: 'select-field'
          }
        }}
        entity={entity}
        fieldsSupplied={childEntityType?.fields}
        mode={props.mode}
        fieldChanged={fieldChanged}
      />
    </View>
  )
}
